import { useCallback } from 'react';
import { useLogicBlocks, LogicBlockIndex } from '@morph-mapper/node-logic';
import { useGraphContext } from '../../context/graph/hook';
import { DrawerKind, NodePickerContextType } from '../../store/ui';
import { err, Result } from 'neverthrow';
import { match } from 'ts-pattern';
import {
  CommandError,
  CommandErrorKind,
  CommandResult,
  createReplaceNodeCommand,
  createAddChildCommand,
  createInsertNodeOnEdgeCommand,
} from '../../lib/commands';
import { createNodeInstance } from '../../lib/node';
import { useStore } from '@/store';
import { useNotify } from '@/hooks';

export const useCommand = () => {
  /** Global state */
  const variant = useStore(({ config: cfg }) => cfg.getVariant());
  /** Context state */
  const [drawer, applyCommand, INode, IEdge] = useGraphContext(
    ({ ui, cmd, graph: g }) => [ui.drawer, cmd.applyCommand, g.node, g.edge]
  );
  /** Hooks */
  const { warn } = useNotify();
  const { logicBlocks } = useLogicBlocks(variant);
  /** Handlers */
  // There is only one output port, so get its id.
  const getPortOutput = useCallback(
    (type: LogicBlockIndex) => {
      return logicBlocks[type].ports.output[0]?.id;
    },
    [logicBlocks]
  );
  // There are multiple input ports, we do a naive selection based on the ordering.
  // TODO: Add resolver logic where the user should confirm or reselect manually.
  const getPortInput = useCallback(
    (type: LogicBlockIndex) => {
      return logicBlocks[type].ports.input[0]?.id;
    },
    [logicBlocks]
  );

  const execute = useCallback(
    (blockType: LogicBlockIndex): Result<CommandResult, CommandError> => {
      if (drawer.kind !== DrawerKind.NodePicker) {
        return err({
          kind: CommandErrorKind.Validation,
          message: 'There is no context available for command execution.',
        });
      }

      const result = match(drawer.context)
        .with({ mode: NodePickerContextType.ReplaceNode }, (ctx) => {
          const nodeId = INode(ctx.nodeId).getId();
          if (!nodeId) {
            return err({
              kind: CommandErrorKind.NotFound,
              message: 'Target node for replace operation does not exist.',
            });
          }

          const newNode = createNodeInstance({ kind: blockType }, logicBlocks);
          return applyCommand(
            createReplaceNodeCommand({
              nodeId,
              created: {
                inputPortId: getPortInput(blockType),
                outputPortId: getPortOutput(blockType),
              },
              node: newNode,
            })
          );
        })
        .with({ mode: NodePickerContextType.InsertOnEdge }, (ctx) => {
          const edgeId = IEdge(ctx.edgeId).getId();
          if (!edgeId) {
            return err({
              kind: CommandErrorKind.NotFound,
              message: 'Target edge for insert operation does not exist.',
            });
          }

          const newNode = createNodeInstance({ kind: blockType }, logicBlocks);
          return applyCommand(
            createInsertNodeOnEdgeCommand({
              edgeId,
              created: {
                inputPortId: getPortInput(blockType),
                outputPortId: getPortOutput(blockType),
              },
              node: newNode,
            })
          );
        })
        .with({ mode: NodePickerContextType.AddChildToNode }, (ctx) => {
          const nodeId = INode(ctx.nodeId).getId();
          if (!nodeId || !ctx.portId) {
            return err({
              kind: CommandErrorKind.NotFound,
              message: 'Target node for add child operation does not exist.',
            });
          }

          const newNode = createNodeInstance({ kind: blockType }, logicBlocks);
          return applyCommand(
            createAddChildCommand({
              created: { portId: getPortOutput(blockType) },
              parent: { nodeId, portId: ctx.portId },
              node: newNode,
            })
          );
        })
        .exhaustive();

      if (result.isErr()) {
        warn(`Failed to execute node picker command: ${result.error}`);
        return result;
      }

      return result;
    },
    [
      applyCommand,
      drawer,
      logicBlocks,
      INode,
      IEdge,
      getPortInput,
      getPortOutput,
      warn,
    ]
  );

  return { execute };
};
